import type { AppDefinition } from './contracts';

const assertUnique = (values: string[], what: string) => {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      throw new Error(`[defineApp] duplicate ${what}: ${value}`);
    }
    seen.add(value);
  }
};

/**
 * Identity function with dev-time validation, so a broken composition fails
 * loudly during development instead of silently dropping a route, a nav item
 * or a settings anchor.
 */
export function defineApp(app: AppDefinition): AppDefinition {
  if (import.meta.env.DEV) {
    const routes = app.modules.flatMap((m) => m.routes ?? []);
    assertUnique(
      routes.map((r) => r.to),
      'route',
    );
    assertUnique(
      app.modules.flatMap((m) => (m.settingsRoutes ?? []).map((r) => r.to)),
      'settings route',
    );
    assertUnique(
      app.modules.flatMap((m) => (m.settingsSections ?? []).map((s) => s.id)),
      'settings section',
    );
    assertUnique(
      app.modules.flatMap((m) => (m.slices ?? []).map((s) => s.reducerPath)),
      'slice',
    );
    assertUnique(
      app.modules.map((m) => m.id),
      'module id',
    );
    const sectionIds = new Set(app.navSections.map((s) => s.id));
    for (const item of app.modules.flatMap((m) => m.nav ?? [])) {
      if (!sectionIds.has(item.sectionId)) {
        throw new Error(
          `[defineApp] nav item ${item.to} points to unknown section "${item.sectionId}"`,
        );
      }
    }
    // The shell's catch-all redirects here; an unmounted target would loop.
    // Each landing must be mounted for the audience that lands on it.
    const mountedFor = (viewer: 'user' | 'internal') =>
      app.modules
        .filter((m) => (m.audience ?? 'user') === 'all' || (m.audience ?? 'user') === viewer)
        .flatMap((m) => m.routes ?? []);
    if (!mountedFor('user').some((r) => r.to === app.routes.landing)) {
      throw new Error(
        `[defineApp] routes.landing "${app.routes.landing}" is not a mounted route`,
      );
    }
    const internalLanding = app.routes.internalLanding;
    if (internalLanding && !mountedFor('internal').some((r) => r.to === internalLanding)) {
      throw new Error(
        `[defineApp] routes.internalLanding "${internalLanding}" is not mounted for internal users`,
      );
    }
  }
  return app;
}
